import { motion, type Variants } from 'framer-motion';
import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ClipLoader } from 'react-spinners';
import { OutlineCheckIcon, OutlineChevronDownIcon } from '@tempmee/react-icons';
import { colors } from '@tempmee/tailwind';
import { Colors } from '~/themes/colors';
import { Box } from '../Box';

export interface ISelectOption {
  title: string;
  value: string;
  PrefixComponent?: ReactNode;
}

export interface IValidationProps {
  [key: string]: unknown;
}

export interface IInputSelectProps {
  value: string;
  setValue: (value: string) => void;
  dataCy?: string;
  dataTestId?: string;
  disabled?: boolean;
  isSelectVisible?: boolean;
  options?: ISelectOption[];
  placeholder?: string;
  validation?: IValidationProps;
  /**
   * Custom title to display instead of looking up from options.
   * Useful when options are temporarily empty (e.g., during refetch)
   * but you want to preserve the display value.
   */
  customTitle?: string;
  /**
   * Show error state (red border)
   */
  error?: boolean;
  /**
   * Show loading state
   */
  isLoading?: boolean;
}

const opacityVariants: Variants = {
  show: { opacity: 1, display: 'flex' },
  hide: { opacity: 0, transitionEnd: { display: 'none' } },
};

export const InputSelect = ({
  dataCy,
  dataTestId,
  disabled = false,
  isSelectVisible = true,
  options = [],
  placeholder = 'Select',
  validation = {},
  value: inputValue,
  setValue,
  customTitle,
  error = false,
  isLoading = false,
}: IInputSelectProps) => {
  const [showOptions, setShowOptions] = useState<boolean>(false);
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);

  const inputRef = useRef<HTMLDivElement>(null);
  const optionsRef = useRef<HTMLDivElement>(null);

  const selectOption: ISelectOption[] = isSelectVisible ? [{ title: placeholder, value: '' }] : [];

  const handleClickOutside = useCallback((event: Event) => {
    if (inputRef.current && !inputRef.current.contains(event.target as Node)) {
      setShowOptions(false);
    }
  }, []);

  useEffect(() => {
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [handleClickOutside]);

  useEffect(() => {
    if (!showOptions || !inputValue || !optionsRef.current) return;

    // INFO: the timeout is necessary to wait for the options to be rendered
    const timeoutId = setTimeout(() => {
      const selectedOption = optionsRef.current?.querySelector(
        `[data-value="${inputValue}"]`,
      ) as HTMLElement;
      const threshold = (optionsRef.current?.clientHeight ?? 0) / 2;

      optionsRef.current?.scrollTo({
        top: (selectedOption?.offsetTop ?? 0) - threshold,
        behavior: 'smooth',
      });
    }, 250);

    return () => clearTimeout(timeoutId);
  }, [showOptions, inputValue]);

  const handleButtonClick = useCallback(() => {
    setShowOptions(true);
  }, []);

  const handleOptionClick = useCallback(
    (value: string) => {
      setShowOptions(false);
      setValue(value);
    },
    [setValue],
  );

  const handleHoverStart = useCallback((value: string) => {
    setHoveredItem(value);
  }, []);

  const handleHoverEnd = useCallback(() => {
    setHoveredItem(null);
  }, []);

  const getCurrentTitle = (): string => {
    // If customTitle is provided, use it
    if (customTitle !== undefined) {
      return customTitle;
    }

    const allOptions = [{ title: '', value: '' }, ...options];
    return allOptions.find(({ value }) => value === inputValue)?.title ?? '';
  };

  const CurrentPrefixComponent = useMemo(() => {
    const allOptions = [{ title: '', value: '' }, ...options];

    const Component = allOptions.find(({ value }) => value === inputValue)?.PrefixComponent;

    if (Component) {
      return Component;
    }

    return null;
  }, [inputValue, options]);

  return (
    <div ref={inputRef} style={{ position: 'relative', width: '100%' }}>
      <div
        className="select-title"
        style={{
          alignItems: 'center',
          width: '100%',
          height: 42,
          backgroundColor: error ? Colors.error_background : Colors.neutral_50,
          border: error ? `1.5px solid ${Colors.error_400}` : 'none',
          borderRadius: 5,
          appearance: 'none',
          ...(disabled && !error && { backgroundColor: Colors.neutral_150 }),
        }}
      >
        <button
          type="button"
          disabled={disabled}
          style={{
            all: 'unset',
            width: '100%',
            height: '100%',
            cursor: disabled ? 'not-allowed' : 'pointer',
          }}
          onClick={handleButtonClick}
        >
          <Box flexDirection="row" alignItems="center" paddingLeft={17} gap={8}>
            {CurrentPrefixComponent}
            <input
              placeholder={placeholder}
              readOnly
              style={{
                fontFamily: 'Nunito',
                margin: '0 50px 0 0',
                fontSize: 16,
                padding: 0,
                width: '100%',
                maxWidth: '-webkit-fill-available',
                boxShadow: 'none',
                color: colors.neutral[600],
                background: 'transparent',
                cursor: disabled ? 'not-allowed' : 'pointer',
                flex: 1,
              }}
              value={getCurrentTitle()}
              data-testid={dataTestId}
              data-cy={dataCy}
              {...validation}
            />
          </Box>
        </button>
        <Box
          position="absolute"
          right={17}
          top="50%"
          transform="translateY(-50%)"
          pointerEvents="none"
          alignItems="center"
        >
          <OutlineChevronDownIcon size={24} color={colors.secondary[500]} />
        </Box>
      </div>

      <motion.div
        ref={optionsRef}
        initial="hide"
        transition={{ duration: 0.1 }}
        animate={showOptions ? 'show' : 'hide'}
        variants={opacityVariants}
        className="select-options"
        style={{
          display: 'flex',
          flexDirection: 'column',
          position: 'absolute',
          overflow: 'auto',
          maxHeight: 260,
          top: 10,
          zIndex: 4,
          minWidth: '100%',
          whiteSpace: 'nowrap',
          backgroundColor: Colors.neutral_150,
          borderRadius: 10,
          boxShadow: '0px 6px 10px 2px rgba(0, 0, 0, 0.15), 0px 2px 3px 0px rgba(0, 0, 0, 0.15)',
          padding: '10px 0px',
        }}
        data-testid="options-container"
      >
        {isLoading ? (
          <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
            <ClipLoader size={30} color={colors.primary[500]} loading />
          </div>
        ) : (
          [...selectOption, ...options].map(({ title, value }) => (
            <motion.div
              data-value={value}
              key={value}
              onHoverStart={() => handleHoverStart(value)}
              onHoverEnd={handleHoverEnd}
              onClick={() => handleOptionClick(value)}
              style={{
                display: 'flex',
                alignItems: 'center',
                backgroundColor: hoveredItem === value ? colors.secondary[500] : undefined,
                borderRadius: 8,
                margin: '0px 10px',
                cursor: 'pointer',
              }}
            >
              {inputValue === value ? (
                <div style={{ width: 24, height: 24, margin: '6px 8px' }}>
                  <OutlineCheckIcon
                    color={hoveredItem === value ? colors.shades[0] : colors.neutral[600]}
                  />
                </div>
              ) : (
                <div style={{ width: 24, height: 24, margin: '6px 8px' }} />
              )}

              <span
                style={{
                  fontFamily: 'Nunito',
                  fontWeight: inputValue === value || hoveredItem === value ? 'bold' : 'normal',
                  fontSize: 16,
                  color: hoveredItem === value ? colors.shades[0] : colors.neutral[600],
                  paddingRight: 20,
                }}
              >
                {title}
              </span>
            </motion.div>
          ))
        )}
      </motion.div>
    </div>
  );
};
